import { usePaused } from "@nexxt/app-kit";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useMe } from "../auth/me";
import { type Queue, useQueues } from "../home/queues";
import { MIN_QUERY, type ParsedQuery, parseQuery, queuePhrases, queuesNamed, vocabulary } from "./query-parser";
import { type SearchGroup, searchQueries } from "./search.queries";
import { readableSearchTypes, type SearchDomain, type SearchTypeName, searchTypes, typeOrder } from "./search-types";

// The search of ⌘K and of the search page (ADR-117): the domains with a type the user can read are asked side by side
// once the typing pauses, and each answers or fails on its own. The groups come back in the order a recognised code
// gives, then in the order of the types.

interface SearchOptions {
  pageSize: number;
  /** One type only (the `type` of the search page); a prefix typed restricts it as well. */
  type?: SearchTypeName | undefined;
  enabled?: boolean;
  /** The text has paused already (the search page, whose URL follows the typing after a pause). */
  settled?: boolean;
}

export interface SearchResults {
  parsed: ParsedQuery<SearchTypeName>;
  /** The type the search is restricted to, by the page or by a prefix. */
  type: SearchTypeName | undefined;
  /** Whether any domain is asked: enough text, and a type the user can read. */
  asked: boolean;
  /** Only the types that found something. */
  groups: SearchGroup[];
  failed: SearchDomain[];
  /** A domain has not answered yet, or the typing has not paused. */
  pending: boolean;
  retry: () => void;
}

export function useSearchResults(
  text: string,
  { pageSize, type: only, enabled = true, settled: given = false }: SearchOptions,
): SearchResults {
  const { permissions } = useMe();
  const paused = usePaused(text.trim());
  const settled = given ? text.trim() : paused;
  const parsed = parseQuery(settled, vocabulary);
  const type = only ?? parsed.type;
  const domains = [...new Set(readableSearchTypes(permissions, type).map((t) => searchTypes[t].domain))];
  const asked = enabled && parsed.q.length >= MIN_QUERY && domains.length > 0;

  const answers = useQueries({
    queries: domains.map((domain) => ({
      ...searchQueries.domain(domain, parsed.q, type, pageSize),
      enabled: asked,
      // the hits of the text before stay until the new ones arrive, so the list does not empty at every pause
      placeholderData: keepPreviousData,
    })),
  });

  const rank = (t: SearchTypeName) => {
    const first = parsed.first.indexOf(t);
    return first >= 0 ? first : parsed.first.length + typeOrder.indexOf(t);
  };
  const groups = answers
    .flatMap((a) => a.data?.groups ?? [])
    .filter((g) => g.items.length > 0)
    .sort((a, b) => rank(a.type) - rank(b.type));

  return {
    parsed,
    type,
    asked,
    groups,
    failed: asked ? domains.filter((_, i) => answers[i]?.isError) : [],
    pending: asked && (settled !== text.trim() || answers.some((a) => a.isPending)),
    retry: () => {
      for (const a of answers) if (a.isError) void a.refetch();
    },
  };
}

/** The home queues whose state the text names ("da approvare"), among those of the user's roles. */
export function useNamedQueues(text: string): Queue[] {
  const { yourTurn, waiting } = useQueues();
  const queues = [...yourTurn, ...waiting];
  // a queue is named by its title too, as the app writes it
  const phrases = Object.fromEntries(queues.map((q) => [q.id, [q.title, ...queuePhrases[q.id]]]));
  const named = queuesNamed(text, phrases);
  return queues.filter((queue) => named.includes(queue.id));
}
